import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../shared/assets';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import { useTranslation } from '../../i18n';
import '../BrandHomePage/HorizontalScrollCarousel/HorizontalScrollCarousel.css';

// Intro Copy Component
interface IntroCopyProps {
  onAboutUsClick: () => void;
}

const IntroCopy: React.FC<IntroCopyProps> = ({ onAboutUsClick }) => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__intro-copy">
      <div className="horizontal-scroll-carousel__intro-bg" />
      <div className="horizontal-scroll-carousel__text-content">
        <div className="horizontal-scroll-carousel__title">
          <p>A real-life oasis, steeped in history</p>
        </div>
        <div className="horizontal-scroll-carousel__description-container">
          <div className="horizontal-scroll-carousel__description">
            <p>At over 2,500 metres above sea level, step into this otherworldly place and feel as if you are on another planet. It's called the Atacama Desert, but there is so much more than just desert. Enjoy breathtaking landscapes by day and world-class stargazing by night.</p>
          </div>
          <HandDrawnButton 
            text="SEE OUR LODGES"
            onClick={onAboutUsClick}
            variant="atacama"
            color="awasi"
            animation="stroke-to-fill"
            className="horizontal-scroll-carousel__button"
            ariaLabel={t('accessibility.buttons.learnMore') + ' - See our lodges'}
          />
        </div>
      </div>
    </div>
  );
};

// Vertical Image Panel
const VerticalImagePanel: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__vertical-panel">
      <img
        src={images.atacamaHeroLandscape || images.atacamaLargeLandscape}
        alt={t('accessibility.images.landscape')}
        className="horizontal-scroll-carousel__vertical-image"
      />
    </div>
  );
};

// Diptych Component
interface DiptychProps {
  onAboutUsClick: () => void;
}

const Diptych: React.FC<DiptychProps> = ({ onAboutUsClick }) => {
  return (
    <div className="horizontal-scroll-carousel__diptych">
      <IntroCopy onAboutUsClick={onAboutUsClick} />
      <VerticalImagePanel />
    </div>
  );
};

// Image Collage
const ImageCollage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__collage">
      <div className="horizontal-scroll-carousel__collage-image1">
        <img
          src={images.atacamaCollage1}
          alt={t('accessibility.images.propertyExterior')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
      <div className="horizontal-scroll-carousel__collage-image2">
        <img
          src={images.atacamaCollage2}
          alt={t('accessibility.images.experience')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
      <div className="horizontal-scroll-carousel__collage-image3">
        <img
          src={images.atacamaCollage3}
          alt={t('accessibility.images.landscape')}
          className="horizontal-scroll-carousel__collage-img"
        />
      </div>
    </div>
  );
};

// Full Screen Panel
const FullScreenPanel: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__fullscreen">
      <img
        src={images.atacamaRoom1 || images.villas13}
        alt={t('accessibility.images.heroImage')}
        className="horizontal-scroll-carousel__fullscreen-image"
      />
    </div>
  );
};

// Outro Component
const Outro: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="horizontal-scroll-carousel__outro">
      <div className="horizontal-scroll-carousel__outro-text">
        <p>
          Awasi Atacama is surrounded by an ornate adobe wall that historically sheltered shepherds and herders. Using local materials and techniques, we built the lodge with adobe, stone and wood.
        </p>
      </div>
      <div className="horizontal-scroll-carousel__outro-image">
        <img
          src={images.atacamaExterior1}
          alt={t('accessibility.images.landscape')}
          className="horizontal-scroll-carousel__outro-img"
        />
      </div>
    </div>
  );
};

const PremiumCarouselAtacama: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleAboutUsClick = () => {
    navigate('/atacama/the-hotel');
  };

  useEffect(() => {
    // Initialize drag scroll with the correct container ID
    const cleanup = initializeDragScroll('draggable-carousel');
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);

  return (
    <section 
      className="horizontal-scroll-carousel" 
      data-scroll-section
      aria-label={t('accessibility.media.carousel')}
    >
      <div 
        ref={scrollRef}
        className="horizontal-scroll-carousel__scroll-container"
        id="draggable-carousel"
        role="region"
        aria-label={t('accessibility.media.carousel')}
      >
        {/* First frame - Diptych */}
        <Diptych onAboutUsClick={handleAboutUsClick} />
        
        {/* Second frame - Image Collage */}
        <ImageCollage />
        
        {/* Third frame - Full Screen Image */}
        <FullScreenPanel />
        
        {/* Fourth frame - Outro */}
        <Outro />
      </div>
    </section>
  );
};

export default PremiumCarouselAtacama;